import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import i18next from 'i18next';

import isEmpty from 'lodash.isempty';
import CountrySelectModal from '../../BankAccountsPageComponents/NewWireWithdrawal/CountrySelectModal';
import AccountDetailsModal from '../../BankAccountsPageComponents/NewWireWithdrawal/AccountDetailsModal';
import ConfirmBankStatementAddress from './ConfirmBankStatementAddress';
import ReviewNewBankAccountModal from '../../BankAccountsPageComponents/NewWireWithdrawal/ReviewNewBankAccountModal';
import Modal from '../../Modal/Modal';
import StepByStep from '../../StepByStep';
import StatusModal from '../StatusModal';

import { submitNewWireAccount } from '../../../store/actions/bankAccount/bankAccountActions';
import { fetchAccountAddresses } from '../../../store/actions/account/accountActions';
import { FETCH_ACCOUNT_ADDRESSES_LOADING } from '../../../store/actions/account/accountActionConstants';
import { selectAccountAddresses } from '../../../store/selectors/accountSelectors';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import {
  selectBankAccountDetailsByRoutingNum,
  selectNewWireAccountData,
} from '../../../store/selectors/bankAccountSelectors';
import {
  closeWizardContent,
  getWizardContent,
  setWizardContent,
} from '../../../util/helpers/wizardHelpers';
import {
  ACCOUNT_DETAILS_INFO,
  ADD_WIRE_WITHDRAWAL_WIZARD,
  BANK_DETAILS_BY_ROUTING_NUMBER,
  ERROR_MESSAGES,
  NEW_WIRE_ACC_DETAILS,
  NEW_WIRE_ACCOUNT_ADDRESS,
  NEW_WIRE_RESPONSE_ERROR,
  NEW_WIRE_RESPONSE_MODAL_OPEN,
  NON_US_COUNTRY_SELECT_MODAL_INFO,
  PREVENT_SUPPORT_MODAL,
  SELECT_TYPE,
  STANDARD_WITHDRAWAL_WIZARD,
  WITHDRAW_MODAL_OPEN,
} from '../../../constants/sessionStorage';
import { useSessionStorageState } from '../../../util/hooks/useSessionStorageState';
import { retrieveFromSessionStorage } from '../../../util/helpers/sessionStorageHelper';
import { formatAddressStringInline } from '../../../util/helpers/addressHelper';
import useGetCountries from '../../../util/hooks/useGetCountries';
import { USA_COUNTRY_LABEL } from '../../../util/constants';
import { BANK_ACCOUNT_ADDED } from '../../../constants/gtmEvents';
import useGtmHook from '../../../util/hooks/useGtmHook';

const AddWireWithdrawalModal = ({
  accountUid,
  isModalOpen,
  setIsModalOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [
    newWireResponseModalOpen,
    setNewWireResponseModalOpen,
  ] = useSessionStorageState(NEW_WIRE_RESPONSE_MODAL_OPEN, false);
  const [newWireError, setNewWireError] = useSessionStorageState(
    NEW_WIRE_RESPONSE_ERROR,
    false,
  );
  const [isLoading, setIsLoading] = useState(false);
  const defaultModalTitle = i18next.t('bankAccounts.modalTitle');
  const [modalTitle, setModalTitle] = useState(defaultModalTitle);

  const addresses = useSelector(selectAccountAddresses);
  const countries = useGetCountries();
  const isLoadingAddresses = useSelector(
    selectIsLoadingByActionType([FETCH_ACCOUNT_ADDRESSES_LOADING]),
  );
  const [withdrawalAddress, setWithdrawalAddress] = useSessionStorageState(
    NEW_WIRE_ACCOUNT_ADDRESS,
    {},
  );

  const [bankAccountDetails, setBankAccountDetails] = useState({});
  const [newWireAccount, setNewWireAccount] = useState({});
  const bankDetails = useSelector(selectBankAccountDetailsByRoutingNum);
  const newWireAcc = useSelector(selectNewWireAccountData);

  const { bankAccountGtmEvent } = useGtmHook();

  const handleGtmEvent = (data) => {
    bankAccountGtmEvent(
      BANK_ACCOUNT_ADDED,
      data?.PurposeType,
      data?.BankAccountType,
      !isEmpty(data?.DomesticBank)
        ? data?.DomesticBank?.Address?.CountryIso3Code
        : data?.InternationalBank?.Address?.CountryIso3Code,
    );
  };

  const handleNewWireOnConfirm = (data) => {
    dispatch(
      submitNewWireAccount({
        accountUid,
        data,
        meta: { setNewWireResponseModalOpen, setNewWireError },
        onSuccess: handleGtmEvent(data),
      }),
    );
    setIsLoading(true);
  };

  useEffect(() => {
    const isSupportPrevented = retrieveFromSessionStorage(
      PREVENT_SUPPORT_MODAL,
    );
    if (newWireResponseModalOpen) {
      setIsLoading(false);
      setIsModalOpen(false);

      if (isSupportPrevented) {
        closeWizardContent();
        setWizardContent(STANDARD_WITHDRAWAL_WIZARD, 1);
        setWizardContent(SELECT_TYPE, 'wire');
        setWizardContent(WITHDRAW_MODAL_OPEN, true);
      }
    }
  }, [newWireResponseModalOpen, setIsModalOpen]);

  useEffect(() => {
    dispatch(fetchAccountAddresses({ accountUid }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (addresses?.length && !withdrawalAddress?.value) {
      const defaultAddress = addresses.find(
        (savedAddress) => savedAddress.IsDefaultMailingAddress,
      );
      setWithdrawalAddress({
        value: defaultAddress?.AddressUid,
        label: formatAddressStringInline(defaultAddress, countries),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses, countries, setWithdrawalAddress]);

  useEffect(() => {
    if (!isEmpty(bankDetails)) {
      setBankAccountDetails(bankDetails);
      setNewWireAccount(newWireAcc);
    } else if (getWizardContent(BANK_DETAILS_BY_ROUTING_NUMBER)) {
      const { country } = getWizardContent(BANK_DETAILS_BY_ROUTING_NUMBER);
      setNewWireAccount({ ...getWizardContent(NEW_WIRE_ACC_DETAILS), country });
      setBankAccountDetails(
        getWizardContent(BANK_DETAILS_BY_ROUTING_NUMBER).routingNumData,
      );
    } else {
      setNewWireAccount({
        ...getWizardContent(NON_US_COUNTRY_SELECT_MODAL_INFO),
        ...getWizardContent(ACCOUNT_DETAILS_INFO),
      });
    }
  }, [bankDetails, newWireAcc]);

  const bankName =
    newWireAccount.country === USA_COUNTRY_LABEL
      ? bankAccountDetails.Name
      : newWireAccount.bankName;

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        size="sm"
        title={modalTitle}
        close={() => {
          setIsModalOpen(false);
          onClose();
        }}
        closeIconVisible
      >
        <StepByStep
          sessionScopeStep={ADD_WIRE_WITHDRAWAL_WIZARD}
          onConfirm={handleNewWireOnConfirm}
        >
          {({ goStepBack, goStepForward, onConfirm }) => [
            <CountrySelectModal
              key="country-select-modal"
              cancel={() => {
                closeWizardContent();
                setIsModalOpen(false);
                onClose();
              }}
              sbsForward={goStepForward}
            />,
            <AccountDetailsModal
              key="account-details-modal"
              sbsBack={goStepBack}
              sbsForward={goStepForward}
            />,
            <ConfirmBankStatementAddress
              key="confirm-new-wire-account-address"
              renderAsModal={false}
              setModalTitle={setModalTitle}
              defaultModalTitle={defaultModalTitle}
              bankName={bankName}
              address={withdrawalAddress}
              addresses={addresses}
              isLoading={isLoadingAddresses}
              setWithdrawalAddress={setWithdrawalAddress}
              wizardAddressKey={NEW_WIRE_ACCOUNT_ADDRESS}
              handleNext={goStepForward}
            />,
            <ReviewNewBankAccountModal
              key="review-new-bank-account-modal"
              sbsBack={goStepBack}
              sbsOnConfirm={onConfirm}
              isLoading={isLoading}
            />,
          ]}
        </StepByStep>
      </Modal>
      <StatusModal
        isOpen={newWireResponseModalOpen}
        hasError={newWireError}
        close={() => setNewWireResponseModalOpen(false)}
        text={
          newWireError
            ? getWizardContent(ERROR_MESSAGES)
            : i18next.t('bankAccounts.newAccountConnected')
        }
        modalTitle={i18next.t('bankAccounts.modalTitle')}
        backButtonText={i18next.t('common.cancel')}
        onButtonClick={
          newWireError
            ? () => {
                onClose();
                closeWizardContent();
                setNewWireResponseModalOpen(false);
                setNewWireError(null);
              }
            : () => {
                onClose();
                closeWizardContent();
                setNewWireResponseModalOpen(false);
                setIsModalOpen(false);
                setNewWireError(null);
              }
        }
      />
    </>
  );
};

AddWireWithdrawalModal.propTypes = {
  accountUid: PropTypes.string,
  isModalOpen: PropTypes.bool,
  setIsModalOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default AddWireWithdrawalModal;
